"use client";

import { useDesktopAnimations } from "@/lib/breakpoints";
import Image from "next/image";
import { motion } from "framer-motion";
import { FaChevronRight } from "react-icons/fa";
import GradientButton from "@/components/ui/GradientButton";

const features = [
  {
    title: "In-House Team",
    description:
      "We have a in-house team of mobile app developers who are well versed in delivering high-end business solutions on several frameworks and technologies.",
    icon: "/images/icons/teama.svg",
  },
  {
    title: "Customer Satisfaction",
    description:
      "We work on the latest frameworks and technologies to deliver you user-friendly, scalable, secure, and new business solutions as per your need.",
    icon: "/images/icons/link.svg",
  },
  {
    title: "Integrity & Transparency",
    description:
      "We respect your ideas and vision, provide you every small project detail regularly, and consider your worthy advice when and as needed.",
    icon: "/images/icons/tech.svg",
  },
  {
    title: "Competitive Pricing",
    description:
      "We offer an ideal combination of cost-effective rates with amazing quality to make sure the lowest pricing in our segment.",
    icon: "/images/icons/hi.svg",
  },
  {
    title: "Flexible Work Models",
    description:
      "Instead of working on unwanted rigid and strict parameters, we offer flexibility to our clients to select from different engagement and hiring models.",
    icon: "/images/icons/badge.svg",
  },
  {
    title: "Agile Procedure",
    description:
      "We work on the Agile methodology and conduct frequent scrum events for optimizing the practices and methods and delivering the finest results.",
    icon: "/images/icons/tin.svg",
  },
];

export default function WhyChooseSection() {
  const desktopAnimations = useDesktopAnimations();
  return (
    <section className="bg-[linear-gradient(#ffe2e2_0%,#fff9f4_100%)] py-12 transition-colors duration-300 sm:py-16 lg:py-20 xl:py-25 dark:bg-none dark:bg-gray-950">
      <div className="mx-auto w-full max-w-[1200px] px-[15px]">
        <div className="mx-auto mb-[30px] max-w-[770px] text-center">
          <h2 className="mb-4 font-poppins text-2xl font-semibold leading-[1.3] text-[#050748] sm:text-3xl md:text-4xl md:leading-[1.3] xl:text-5xl dark:text-white">
            Why Choose The Dezine
          </h2>

          <p className="font-sans text-[16px] font-normal leading-7 text-[#6a6a8e] dark:text-gray-400">
            Being a well-known mobile application development company, The Dezine
            is recognized for serving modern business needs and delivering the
            best-in-class solutions at a competitive cost. Here&apos;s why we can be
            the best choice when it comes to building mobile applications.
          </p>
        </div>

        {/* Live: .s-block.wide-sblock — icon + content side by side */}
        <div className="grid grid-cols-1 gap-[30px] sm:grid-cols-2 lg:grid-cols-3">
          {features.map((item, index) => (
            <motion.div
              key={item.title}
              initial={desktopAnimations ? ({ opacity: 0, y: 20 }) : false}
              whileInView={desktopAnimations ? ({ opacity: 1, y: 0 }) : undefined}
          animate={desktopAnimations ? undefined : ({ opacity: 1, y: 0 })}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
              className="
                inline-flex items-start rounded-lg border border-[#d4f0ff]
                bg-white px-4 py-6 text-left sm:px-5 sm:py-[30px]
                shadow-[0_3.4px_2.7px_-30px_rgba(0,0,0,0.06),0_8.2px_8.9px_-30px_rgba(0,0,0,0.07),0_25px_40px_-30px_rgba(0,0,0,0.2)]
                transition-[transform,box-shadow] duration-[1.2s] ease-[cubic-bezier(0.17,0.85,0.438,0.99)]
                dark:border-gray-700 dark:bg-gray-800
              "
            >
              <div className="shrink-0">
                <Image
                  src={item.icon}
                  alt=""
                  width={40}
                  height={40}
                  className="size-9 object-contain sm:size-10"
                />
              </div>

              <div className="ml-4 min-w-0 flex-1 sm:ml-5">
                <h3 className="mb-2.5 font-poppins text-lg font-semibold leading-7 text-[#050748] sm:text-[22px] sm:leading-8 dark:text-white">
                  {item.title}
                </h3>
                <p className="font-sans text-base font-normal leading-7 text-[#6a6a8e] sm:text-[17px] dark:text-gray-400">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Live: .-cta-btn.mt70 + .free-cta-title */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-[30px] gap-y-4 text-center sm:mt-[70px]">
          <p className="font-poppins text-xl font-normal leading-tight text-[#050748] sm:text-2xl xl:text-4xl dark:text-white">
            Let&apos;s Start a <span className="font-bold">New Project</span> Together
          </p>

          <GradientButton
            href="/contact"
            variant="pink-purple"
          >
            Inquire Now
            <FaChevronRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
          </GradientButton>
        </div>
      </div>
    </section>
  );
}
